import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import icoStyledLinkSmall from "src/assets/images/icoStyledLinkSmall.svg";
import { useAppSelector } from "src/store/actions/action";
import { setFilterFromStateList, setListData } from "src/store/reducers/fulfillListReduce";
import { setNoticeSlideIndex } from "src/store/reducers/homeReduce";
import { lPad } from "src/utils/lPad";
import styled from "styled-components";
import Box from "../common/Box";

interface IHomeNoticeListProps {
  data: any[];
}

const NoticeSlide = (props: IHomeNoticeListProps) => {
  // 초기 값 세팅
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const savedSlideIndex = useAppSelector((state) => state.homeReduce.noticeSlideIndex);

  const [noticeListData, setNoticeListData] = useState(props.data);
  const [bgPercent, setBgPercent] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(savedSlideIndex || 0);

  useEffect(() => {
    if (noticeListData) {
      const totalSlide = noticeListData.length;
      const slidePercent = ((currentSlide + 1) / totalSlide) * 100;
      setBgPercent(Math.round(slidePercent));
    }
  }, [currentSlide, noticeListData]);

  const setNoticeSlide = {
    useTransform: false,
    dots: false,
    speed: 100,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    cssEase: "ease-out",
    infinite: true,
    touchThreshold: 20,
    initialSlide: savedSlideIndex || 0,
    beforeChange: function (oldIdx: number, newIdx: number) {
      setCurrentSlide(newIdx);
      dispatch(setNoticeSlideIndex(newIdx));
    },
  };

  const moveToDetail = (nxInfo: any, userType: string) => {
    // 상세페이지로 이동하기 전 현재 슬라이드 인덱스 저장
    dispatch(setNoticeSlideIndex(currentSlide));
    
    if (userType === "S") {
      if (nxInfo.execute_yn === "Y") {
        navigate(`/fulfill-detail/` + nxInfo.execute_idx);
      } else {
        if (nxInfo.re_execute_yn === "Y") {
          navigate(`/fulfill-detail/` + nxInfo.execute_idx);
        } else {
          navigate(`/notice-detail/` + nxInfo.notice_idx);
        }
      }
    } else {
      navigate(`/notice-detail/` + nxInfo.notice_idx);
    }
  };

  // stateType은 카드에 표시되는 라벨("평가 완료", "평가 대기", "이행 전" 등)이고,
  // fulfillSearchArray/setFilterFromStateList에 넣는 값은 내부 필터 키("평가 전", "미이행" 등)로 서로 다름 (표시 라벨 != 내부 키)
  const linkWithState = async (e: React.MouseEvent<HTMLAnchorElement>, noticeIdx: string, stateType: string) => {
    e.preventDefault();
    if (stateType === "전체") {
      dispatch(setListData({ fulfillSearchArray: [] }));
      dispatch(
        setFilterFromStateList({
          key: "전체",
          data1: [
            { type: "이행", checked: false },
            { type: "미이행", checked: false },
          ],
          data2: [
            { type: "평가 전", checked: false },
            { type: "평가 완료", checked: false },
          ],
        }),
      );
    } else if (stateType === "평가 완료") {
      dispatch(setListData({ fulfillSearchArray: ["이행", "평가 완료"] }));
      dispatch(
        setFilterFromStateList({
          key: "평가 완료",
          data: [
            { type: "평가 전", checked: false },
            { type: "평가 완료", checked: true },
          ],
        }),
      );
    } else if (stateType === "평가 대기") {
      dispatch(setListData({ fulfillSearchArray: ["이행", "평가 전"] }));
      dispatch(
        setFilterFromStateList({
          key: "평가 전",
          data: [
            { type: "평가 전", checked: true },
            { type: "평가 완료", checked: false },
          ],
        }),
      );
    } else if (stateType === "이행 전") {
      dispatch(setListData({ fulfillSearchArray: ["미이행"] }));
      dispatch(
        setFilterFromStateList({
          key: "미이행",
          data: [
            { type: "이행", checked: false },
            { type: "미이행", checked: true },
          ],
        }),
      );
    }

    dispatch(
      setListData({
        keywordSearchData: "",
        currentPageNumber: 1,
      }),
    );
    await navigate(`/fulfill-list/` + noticeIdx);
  };

  return (
    <div style={{ position: "relative" }}>
      {noticeListData && noticeListData.length > 0 ? (
        <>
          <NoticeSlider {...setNoticeSlide}>
            {noticeListData.map((v: any, i: number) => {
              return (
                <NoticeSlideItem key={i}>
                  <div onClick={() => moveToDetail(v, userInfo.user_type)} className={classNames({ isShop: userInfo.user_type === "S" })}>
                    <p className="cate">
                      {v.cate_name} {userInfo.user_type === "S" && v.re_execute_yn === "Y" ? <span className="markRe">재이행</span> : ""}
                    </p>
                    <p className="title">{v.title ? v.title : "제목없음"}</p>
                    <p className="sub-info">
                      <span className="date">{moment(v.insert_date).format("YYYY.MM.DD")}</span>
                      <span className="end-date">{moment(v.end_date).format("YYYY.MM.DD")} 마감</span>
                    </p>
                  </div>
                  {userInfo.user_type === "E" && (
                    <MainDash>
                      <dt>이행현황</dt>
                      <dd>
                        <ul>
                          <li>
                            <a onClick={(e) => linkWithState(e, v.notice_idx, "전체")}>
                              <p>{v.total_execute_count ?? 0}</p>
                              <span>전체</span>
                            </a>
                          </li>
                          <li>
                            <a onClick={(e) => linkWithState(e, v.notice_idx, "평가 완료")}>
                              <p>{v.assess_count ?? 0}</p>
                              <span>평가 완료</span>
                            </a>
                          </li>
                          <li className="mark">
                            <a onClick={(e) => linkWithState(e, v.notice_idx, "평가 대기")}>
                              <p>{v.no_assess_count ?? 0}</p>
                              <span>평가 대기</span>
                            </a>
                          </li>
                          <li>
                            <a onClick={(e) => linkWithState(e, v.notice_idx, "이행 전")}>
                              <p>{v.no_execute_count ?? 0}</p>
                              <span>이행 전</span>
                            </a>
                          </li>
                        </ul>
                      </dd>
                    </MainDash>
                  )}
                </NoticeSlideItem>
              );
            })}
          </NoticeSlider>

          <CustomPaging>
            <span>{lPad(currentSlide + 1, 2, 0)}</span>
            {lPad(noticeListData && noticeListData.length, 2, 0)}
          </CustomPaging>

          {noticeListData && noticeListData.length > 1 && (
            <Box pd="0 15px">
              <SlickProgress bgPercent={bgPercent}></SlickProgress>
            </Box>
          )}
        </>
      ) : (
        <>{userInfo.user_type === "E" ? <NoData>진행중인 공지가 없습니다.</NoData> : <NoData>이행할 공지가 없습니다.</NoData>}</>
      )}
    </div>
  );
};

const NoData = styled.p`
  margin: 0 15px;
  padding: 55px 15px;
  border-radius: 4px;
  text-align: center;
  background-color: ${({ theme }) => theme.color.bgPrimary};
  color: #000;
`;

const NoticeSlider = styled(Slider)`
  padding-left: 15px;
  .slick-slide {
    padding-right: 15px;
  }
  .slick-slide.slick-active {
    z-index: 1;
  }
  .slick-dots {
    position: absolute;
    top: -36px;
    right: 15px;
    bottom: auto;
    display: inline-block;
    width: auto;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: center;
  }
`;

const NoticeSlideItem = styled.div`
  padding: 15px;
  background: ${({ theme }) => theme.color.bgPrimary};
  border-radius: 4px;
  font-size: 14px;
  > div {
    display: inline-block;
    width: 100%;
    padding-right: 16px;
    background: url(${icoStyledLinkSmall}) no-repeat right 3px;

    &.isShop {
      padding-right: 0;
      background: none;
    }

    .cate {
      position: relative;
      margin-bottom: 4px;
      line-height: 24px;
      .markRe {
        position: absolute;
        right: 0;
        display: inline-block;
        padding: 4px 10px;
        background-color: ${({ theme }) => theme.color.main};
        color: #fff;
        line-height: 16px;
      }
    }
    .title {
      color: #000000;
      font-weight: 500;
      line-height: 20px;
      ${({ theme }) => theme.textOverflow.multiLine(2)}
    }
    .sub-info {
      margin-top: 8px;
      font-family: "Poppins";
      font-size: 12px;
      color: ${({ theme }) => theme.color.tertiary};
      .end-date {
        position: relative;
        padding-left: 8px;
        margin-left: 8px;
        color: ${({ theme }) => theme.color.warning};
        font-weight: 500;
        &:before {
          content: "";
          position: absolute;
          top: 50%;
          left: -1px;
          display: block;
          width: 2px;
          height: 12px;
          margin-top: -6px;
          background: ${({ theme }) => theme.color.lineSecondary};
        }
      }
    }
  }
`;

const MainDash = styled.dl`
  margin-top: 10px;
  padding-top: 15px;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  > dt {
    margin-bottom: 10px;
    color: ${({ theme }) => theme.color.tertiary};
  }
  > dd {
    > ul {
      ${({ theme }) => theme.flexBox.flexMixin("row", "space-between", "flex-start")}
      > li {
        width: calc(25% - 6px);
        padding: 10px 4px;
        border-radius: 4px;
        background: #ffffff;
        box-shadow: 2px 4px 4px rgba(0, 0, 0, 0.1);
        color: #000000;
        text-align: center;
        p {
          margin-bottom: 4px;
          font-family: "Poppins";
          font-size: 20px;
          line-height: 30px;
          font-weight: 500;
        }
        span {
          font-size: 12px;
          white-space: nowrap;
        }
        &.mark {
          background: ${({ theme }) => theme.color.main};
          color: #ffffff;
        }
      }
    }
  }
`;

const CustomPaging = styled.p`
  position: absolute;
  right: 15px;
  top: -32px;
  color: ${({ theme }) => theme.color.quaternary};
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 400;
  > span {
    position: relative;
    display: inline-block;
    padding-right: 8px;
    margin-right: 8px;
    color: ${({ theme }) => theme.color.main};
    font-weight: 700;
    &:after {
      content: "";
      display: block;
      position: absolute;
      right: -1px;
      top: 50%;
      width: 2px;
      height: 12px;
      margin-top: -6px;
      border-radius: 1px;
      background: ${({ theme }) => theme.color.lineSecondary};
    }
  }
`;

const SlickProgress = styled.div<{ bgPercent: number }>`
  overflow: hidden;
  display: block;
  width: 100%;
  height: 3px;
  margin-top: 16px;
  border-radius: 10px;

  background-color: ${({ theme }) => theme.color.bgPrimary};
  background-image: linear-gradient(to right, #00263e, #00263e);
  background-repeat: no-repeat;
  background-size: ${({ bgPercent }) => (bgPercent ? bgPercent + "%" : "0%")} 100%;

  transition: background-size 0.3s ease-in-out;
`;

export default NoticeSlide;
